import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { Button } from '@shared/ui/button/button';
import { Modal } from '@shared/ui/modal/modal';
import { useAudioControls } from '../audio-controls-provider';
import { useWebAudioContext } from '@shared/context/WebAudio';
import {
	IconPlayerPlay,
	IconPlayerStop,
	IconPlus,
	IconMinus,
	IconArrowsDiagonal,
	IconVolume2,
} from '@tabler/icons-react';
import s from './metronome.module.scss';
import { createMetronomeBuffer } from '../../lib/generateMetronome';
import { JUKEBOX_KINDS } from '../audio-controls-provider';
import { JukeboxVolumeControl } from '../jukebox-volume-control/jukebox-volume-control';
import { useHeardDelayMs } from '../../lib/heard-beat-calibrator';

const MIN_BPM = 40;
const MAX_BPM = 240;

/** Пояснение для блокировки запуска при выключенном микрофоне. */
const MIC_OFF_TOOLTIP =
	'Включите микрофон: метроном и музыка идут собеседнику через аудиотрек занятия, без микрофона их не слышно';
const MIC_OFF_TOOLTIP_ID = 'tooltip-sync-audio-mic-metronome';

export const Metronome = () => {
	const audioContext = useWebAudioContext();
	const { startMusic, stopMusic, someoneElsePlayingJukeBox, jukeBoxKind, amIPlayingJukeBox, remoteSync, micEnabled } = useAudioControls();
	// Громкость локального мониторинга метронома (у слушателя отдельной
	// регулировки нет: метроном смикширован с голосом ведущего в один трек)
	const volumeKind = JUKEBOX_KINDS.metronome;

	const [bpm, setBpm] = useState(120);
	const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [currentBeat, setCurrentBeat] = useState(-1);
	const [isExpanded, setIsExpanded] = useState(false);

	const rafRef = useRef(null);
	const startTimeRef = useRef(0);
	const lastBeatRef = useRef(-1);
	const isMountedRef = useRef(true);
	const isPlayingRef = useRef(false);

	// Смещение фазы долей на реальную задержку звука: калибровщик слушает
	// слышимые клики в удалённом миксе (сеть + джиттер-буфер) и подгоняет D.
	// Без неё визуал бежит впереди звука на ~50–150 мс.
	const heardDelayMs = useHeardDelayMs();

	// Доля метронома собеседника: считаем по wall-clock от remoteSync.wallStart
	// (уже переведённому в наши часы) минус калиброванная задержка D
	const [remoteBeat, setRemoteBeat] = useState(-1);
	const remoteMetronomeActive =
		someoneElsePlayingJukeBox &&
		jukeBoxKind === JUKEBOX_KINDS.metronome &&
		remoteSync?.action === 'start' &&
		remoteSync?.kind === 'metronome' &&
		remoteSync?.bpm > 0;

	useEffect(() => {
		if (!remoteMetronomeActive) {
			setRemoteBeat(-1);
			return undefined;
		}
		const { wallStart, bpm, beatsPerMeasure } = remoteSync;
		const beatMs = (60 / bpm) * 1000;
		let raf = null;
		let lastBeat = -1;
		const tick = () => {
			const elapsed = Date.now() - wallStart - heardDelayMs;
			if (elapsed >= 0) {
				const beat = Math.floor(elapsed / beatMs) % beatsPerMeasure;
				if (beat !== lastBeat) {
					lastBeat = beat;
					setRemoteBeat(beat);
				}
			}
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => {
			if (raf) cancelAnimationFrame(raf);
		};
	}, [remoteMetronomeActive, remoteSync, heardDelayMs]);

	const beatInterval = 60 / bpm;

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	useEffect(() => {
		isPlayingRef.current = isPlaying;
	}, [isPlaying]);

	const updateVisuals = useCallback(() => {
		if (!isMountedRef.current || !isPlayingRef.current) return;
		const elapsed = audioContext.currentTime - startTimeRef.current;
		const totalBeats = Math.floor(elapsed / beatInterval);
		const beat = totalBeats % beatsPerMeasure;
		if (beat !== lastBeatRef.current) {
			lastBeatRef.current = beat;
			setCurrentBeat(beat);
		}
		rafRef.current = requestAnimationFrame(updateVisuals);
	}, [audioContext, beatInterval, beatsPerMeasure]);

	useEffect(() => {
		if (isPlaying) {
			rafRef.current = requestAnimationFrame(updateVisuals);
		}
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, [isPlaying, updateVisuals]);

	const start = async () => {
		if (isLoading || isPlaying || micEnabled === false) return;
		setIsLoading(true);
		try {
			const measures = 30;
			const duration = measures * beatsPerMeasure * beatInterval;
			// Синтезируем тики сразу в буфер живого контекста: старт без задержек
			// рендера/декодирования и без браузерно-зависимых сбоев на этих этапах.
			// Акцент на сильной доле каждого такта (beatsPerMeasure).
			const buffer = createMetronomeBuffer(audioContext, duration, bpm, { beatsPerMeasure });
			if (!isMountedRef.current) return;
			const startedAt = await startMusic(0, buffer, true, JUKEBOX_KINDS.metronome, {
				bpm,
				beatsPerMeasure,
			});
			if (!isMountedRef.current) return;
			// Визуал считаем от фактического момента старта звука,
			// чтобы доли совпадали со звуком у всех участников.
			startTimeRef.current = startedAt ?? audioContext.currentTime;
			lastBeatRef.current = -1;
			setIsPlaying(true);
			setCurrentBeat(0);
		} catch (error) {
			console.error('Ошибка запуска метронома:', error);
		} finally {
			if (isMountedRef.current) setIsLoading(false);
		}
	};

	const stop = () => {
		setIsPlaying(false);
		setCurrentBeat(-1);
		lastBeatRef.current = -1;
		stopMusic();
	};

	const changeBpm = (delta) => {
		setBpm((prev) => {
			const next = prev + delta;
			return Math.min(MAX_BPM, Math.max(MIN_BPM, next));
		});
	};

	useEffect(() => {
		if (isPlaying) {
			stop();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bpm, beatsPerMeasure]);

	// Трансляцию могли остановить снаружи (переключение вкладок вызывает stopMusic) —
	// синхронизируем локальное состояние, иначе визуал «бежит» без звука,
	// а повторный старт блокируется флагом isPlaying
	useEffect(() => {
		if (!amIPlayingJukeBox && isPlaying) {
			setIsPlaying(false);
			setCurrentBeat(-1);
			lastBeatRef.current = -1;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [amIPlayingJukeBox]);

	const renderBeatIndicators = (compact = false, activeBeat = currentBeat, count = beatsPerMeasure) => {
		const beatClass = compact ? s.compactBeat : s.beat;
		const rowClass = compact ? s.compactBeats : s.beatsRow;
		return (
			<div className={rowClass}>
				{Array.from({ length: count }).map((_, i) => (
					<div
						key={i}
						className={`${beatClass} ${activeBeat === i ? s.active : ''} ${i === 0 ? s.accent : ''}`}
					/>
				))}
			</div>
		);
	};

	return (
		<>
			<Tooltip id={MIC_OFF_TOOLTIP_ID} content={MIC_OFF_TOOLTIP} />
			{/* Компактный режим — одна строка */}
			<div className={s.compact}>
				{someoneElsePlayingJukeBox ? (
					<div className={s.compactOccupied}>
						<IconVolume2 size={20} className={s.compactOccupiedIcon} />
						<span className={s.compactOccupiedText}>
							Метроном собеседника{remoteSync?.bpm ? ` · ${remoteSync.bpm} BPM` : ''}
						</span>
						{renderBeatIndicators(true, remoteBeat, remoteSync?.beatsPerMeasure)}
					</div>
				) : (
					<>
						<span
								data-tooltip-id={micEnabled === false && !isPlaying ? MIC_OFF_TOOLTIP_ID : undefined}
							>
							<button
								className={s.compactPlayBtn}
								onClick={isPlaying ? stop : start}
								disabled={isLoading || (micEnabled === false && !isPlaying)}
								type="button"
							>
								{isPlaying ? <IconPlayerStop size={18} /> : <IconPlayerPlay size={18} />}
							</button>
						</span>

						<button
							className={s.compactIconBtn}
							onClick={() => changeBpm(-1)}
							disabled={bpm <= MIN_BPM || isLoading}
							type="button"
						>
							<IconMinus size={16} />
						</button>

						<span className={s.compactBpm}>{bpm}</span>

						<button
							className={s.compactIconBtn}
							onClick={() => changeBpm(1)}
							disabled={bpm >= MAX_BPM || isLoading}
							type="button"
						>
							<IconPlus size={16} />
						</button>

						{renderBeatIndicators(true)}

						<JukeboxVolumeControl kind={volumeKind} className={s.compactVolume} />
					</>
				)}

				<button
					className={`${s.compactIconBtn} ${s.compactExpandBtn}`}
					onClick={() => setIsExpanded(true)}
					type="button"
					title="Развернуть"
				>
					<IconArrowsDiagonal size={16} />
				</button>
			</div>

			{/* Развёрнутый режим — модалка */}
			<Modal isOpen={isExpanded} close={() => setIsExpanded(false)} title="Метроном">
				<div className={s.expanded}>
					{someoneElsePlayingJukeBox ? (
						<div className={s.expandedOccupied}>
							<IconVolume2 size={40} className={s.expandedOccupiedIcon} />
							<p className={s.expandedOccupiedText}>
								Метроном собеседника{remoteSync?.bpm ? ` · ${remoteSync.bpm} BPM` : ''}
							</p>
							{renderBeatIndicators(false, remoteBeat, remoteSync?.beatsPerMeasure)}
						</div>
					) : (
						<>
							<div className={s.display}>
								<div className={s.bpmValue}>{bpm}</div>
								<div className={s.bpmLabel}>BPM</div>
							</div>

							{renderBeatIndicators(false)}

							<div className={s.bpmControl}>
								<button
									className={s.iconBtn}
									onClick={() => changeBpm(-1)}
									disabled={bpm <= MIN_BPM || isLoading}
									type="button"
								>
									<IconMinus size={20} />
								</button>
								<input
									type="range"
									min={MIN_BPM}
									max={MAX_BPM}
									value={bpm}
									onChange={(e) => setBpm(Number(e.target.value))}
									className={s.slider}
									disabled={isLoading}
								/>
								<button
									className={s.iconBtn}
									onClick={() => changeBpm(1)}
									disabled={bpm >= MAX_BPM || isLoading}
									type="button"
								>
									<IconPlus size={20} />
								</button>
							</div>

							<div className={s.beatsSelect}>
								<span className={s.label}>Долей в такте</span>
								<div className={s.beatsOptions}>
									{[2, 3, 4, 5, 6, 7, 8].map((n) => (
										<button
											key={n}
											type="button"
											className={`${s.beatOption} ${beatsPerMeasure === n ? s.beatOptionActive : ''}`}
											onClick={() => setBeatsPerMeasure(n)}
											disabled={isLoading}
										>
											{n}
										</button>
									))}
								</div>
							</div>

							<div className={s.volumeRow}>
						<span className={s.label}>Громкость метронома</span>
						<JukeboxVolumeControl kind={volumeKind} />
					</div>

					<div className={s.playControl}>
							<span
								data-tooltip-id={micEnabled === false && !isPlaying ? MIC_OFF_TOOLTIP_ID : undefined}
							>
									<Button
										className={s.playBtn}
										onClick={isPlaying ? stop : start}
										label={isPlaying ? 'Стоп' : 'Старт'}
										lefticon={isPlaying ? <IconPlayerStop size={22} /> : <IconPlayerPlay size={22} />}
										disabled={isLoading || (micEnabled === false && !isPlaying)}
										fetching={isLoading}
									/>
							</span>
							</div>
						</>
					)}
				</div>
			</Modal>
		</>
	);
};
